import React, { useContext, useState } from "react"
import { useChampionContext } from "./ChampionContext"
import { useIgnoredContext } from "./IgnoredContext"

import TYPES from "../data/common/types.json"
import SUBTYPES from "../data/common/subtypes.json"
import RARITIES from "../data/common/rarities.json"
import ELEMENTS from "../data/common/elements.json"
import ADVANCED_ELEMENTS from "../data/common/advanced_elements.json"
import SET_NAMES from "../data/common/setNames.json"
import KEYWORDS from "../data/common/keywords.json"

import CARDS from "../data/deck/cards.json"

import FLOATING from "../data/manual/floating-mem.json"
import PREPCOUNT from "../data/manual/prep-counters.json"
import MILLCARDS from "../data/manual/mill-cards.json"
import GRAVEBANISH from "../data/manual/grave-banish.json"
import CLASSES from "../data/common/classes.json"

import { localInventory } from "../utils/local-inventory"
import { isSingleOnlyCard } from "../utils/isSingleOnlyCard"
import { useAuthContext } from "./AuthContext"

const allCards = CARDS.sort((a, b) => {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
})

SET_NAMES.sort((a, b) => {
  return a.toLowerCase().localeCompare(b.toLowerCase())
})

function getElementOrder() {
  const elClones = [...ELEMENTS]
  let order = ["NORM", "FIRE", "WIND", "WATER"]
  elClones.forEach((el) => {
    if (!order.includes(el)) {
      order.push(el)
    }
  })
  order = order.reduce((acc, curr, idx) => {
    acc[curr] = idx
    return acc
  }, {})
  return order
}
const ELEMENT_ORDER = getElementOrder()

const allCardsMap = allCards.reduce((acc, curr) => {
  acc[curr.name] = curr
  return acc
}, {})

function formatMoney(n) {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const fullCollectionValue = formatMoney(
  allCards.reduce((acc, card) => {
    const playsetSize = isSingleOnlyCard(card) ? 1 : 4
    return acc + (card.computed?.tcgLowestPrice ?? 0) * playsetSize
  }, 0),
)

function fallbackInfinity(num) {
  return num ?? Infinity
}

// percentile cutoffs over the cards that have a 60-day win rate;
// wr60 is absent when a card saw no play in the window. All cutoffs
// are undefined if the data ships without any win rates:
const META_TIER_CUTOFFS = (() => {
  const rates = allCards
    .map((c) => c.computed?.meta?.wr60)
    .filter((wr) => wr != null)
    .sort((a, b) => b - a)
  const at = (p) => rates[Math.min(Math.floor(rates.length * p), rates.length - 1)]
  return { top5: at(0.05), top25: at(0.25), top50: at(0.5), bottom25: at(0.75) }
})()

// actual win-rate threshold behind a tier, for tooltips;
// null when the data shipped without wr60 values:
const cutoffLabel = (op, cut) => (cut == null ? null : `${op} ${cut}%`)

// single source of truth for the Metagame tier picker: option
// values, menu labels, section badge, thresholds, and filter
// predicates. needsCutoffs marks tiers that are meaningless
// without wr60 data.
export const META_TIERS = [
  { value: "all", name: "All", badge: null },
  // prettier-ignore
  { value: "staple", name: "Staple", detail: "(top 5%)", badge: "Staple", threshold: cutoffLabel("≥", META_TIER_CUTOFFS.top5), needsCutoffs: true, inTier: (wr) => wr >= META_TIER_CUTOFFS.top5 },
  // prettier-ignore
  { value: "popular", name: "Popular", detail: "(top 25%)", badge: "Popular", threshold: cutoffLabel("≥", META_TIER_CUTOFFS.top25), needsCutoffs: true, inTier: (wr) => wr >= META_TIER_CUTOFFS.top25 },
  // prettier-ignore
  { value: "playable", name: "Playable", detail: "(top 50%)", badge: "Playable", threshold: cutoffLabel("≥", META_TIER_CUTOFFS.top50), needsCutoffs: true, inTier: (wr) => wr >= META_TIER_CUTOFFS.top50 },
  // prettier-ignore
  { value: "fringe", name: "Fringe", detail: "(bottom 50%)", badge: "Fringe", threshold: cutoffLabel("<", META_TIER_CUTOFFS.top50), needsCutoffs: true, inTier: (wr) => wr < META_TIER_CUTOFFS.top50 },
  // prettier-ignore
  { value: "hipster", name: "Hipster", detail: "(bottom 25%)", badge: "Hipster", threshold: cutoffLabel("<", META_TIER_CUTOFFS.bottom25), needsCutoffs: true, inTier: (wr) => wr < META_TIER_CUTOFFS.bottom25 },
  // prettier-ignore
  { value: "never", name: "Never", detail: "(0%)", badge: "Never", threshold: "exactly 0%", inTier: (wr) => wr === 0 },
]

const getNodes = (sortDir, a, b) => {
  return sortDir === "asc" ? { first: a, second: b } : { first: b, second: a }
}

const CardsContext = React.createContext(null)

const rarityOpts = RARITIES.map((r) => r.text)
export const sortOpts = [
  "ALPHABETICALLY",
  "PLAY COST",
  "ELEMENT",
  "PRINT RUN",
  "TCGPLAYER COST",
  "WIN RATE",
  "INVENTORY",
]
export const sortDirOpts = ["asc", "desc"]

const initInventory = allCards.reduce((acc, curr) => {
  acc[curr.name] = 0
  return acc
}, {})

function updateInventoryWithLatest(curr) {
  allCards.forEach((c) => {
    if (!curr[c.name]) {
      curr[c.name] = 0
    }
  })
  return curr
}

function startInventory() {
  const curr = localInventory.load()
  if (curr) {
    return updateInventoryWithLatest(curr)
  }
  return initInventory
}

// both maps are dense (every card padded to at least 0), so one pass
// over the keys of `a` covers everything
function sameCounts(a, b) {
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length) {
    return false
  }
  return keys.every((k) => a[k] === b[k])
}

export const CardsContextProvider = ({ children }) => {
  const [inventoryEnabled, setInventoryEnabled] = useState(false)
  const [inventory, setInventory] = useState(startInventory)

  const authCtx = useAuthContext()
  const user = authCtx?.user
  const cloudLoaded = authCtx?.cloudLoaded
  const cloudDoc = authCtx?.cloudDoc

  // marks renders whose inventory came from a cloud snapshot (must not
  // be diffed back into the write queue) and full CSV replaces (pushed
  // as one set instead of per-card deltas)
  const appliedFromCloudRef = React.useRef(false)
  const fullReplaceRef = React.useRef(null)
  const prevInventoryRef = React.useRef(null)

  // every inventory write is backfilled here so cards released after
  // a snapshot was taken (CSV upload, older localStorage) can never
  // be missing a count — writers outside the provider don't need to
  // remember to normalize
  function setInventoryNormalized(next) {
    setInventory((curr) =>
      updateInventoryWithLatest(
        typeof next === "function" ? next(curr) : next,
      ),
    )
  }

  // full-collection swap (CSV upload). Logged in it becomes ONE cloud
  // set (never per-card deltas); logged out it falls through to the
  // localStorage write like any other edit.
  function replaceInventory(next) {
    const normalized = updateInventoryWithLatest({ ...next })
    fullReplaceRef.current = normalized
    setInventory(normalized)
  }

  // hey codex:
  // invoke IgnoreContext's hook here
  const { ignoredSet } = useIgnoredContext()

  const champCtx = useChampionContext()
  // const isSlimeSpirit = champCtx?.focus?.some(
  //   (c) => c.name === "Spirit of Slime"
  // )
  // const isSlimeSilvie = champCtx?.focus?.some(
  //   (c) => c.name === "Silvie, Slime Sovereign"
  // )

  const estInventoryValue = React.useMemo(
    () =>
      formatMoney(
        Object.entries(inventory).reduce((acc, [name, count]) => {
          return (
            acc + (allCardsMap[name]?.computed?.tcgLowestPrice ?? 0) * count
          )
        }, 0),
      ),
    [inventory],
  )

  const estMissingPlaysetValue = React.useMemo(
    () =>
      formatMoney(
        Object.entries(inventory).reduce((acc, [name, count]) => {
          const card = allCardsMap[name]
          if (!card) return acc
          const playsetSize = isSingleOnlyCard(card) ? 1 : 4
          const missing = Math.max(0, playsetSize - count)
          return acc + (card.computed?.tcgLowestPrice ?? 0) * missing
        }, 0),
      ),
    [inventory],
  )

  // logged in: apply cloud snapshots. Un-flushed local deltas are
  // overlaid so a server ack racing new edits can never revert them.
  // The cloud copy stores only nonzero counts; updateInventoryWithLatest
  // re-pads the zeros the rest of the app expects.
  React.useEffect(() => {
    if (!user || !cloudLoaded || !cloudDoc) {
      return
    }
    const sync = authCtx.getSync()
    const merged = updateInventoryWithLatest({
      ...(cloudDoc.inventory ?? {}),
      ...(sync?.pendingInventory() ?? {}),
    })
    setInventory((curr) => {
      // skip-if-equal keeps the identity stable so own-write acks
      // don't churn the big `cards` memo
      if (sameCounts(curr, merged)) {
        return curr
      }
      appliedFromCloudRef.current = true
      return merged
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, cloudLoaded, cloudDoc])

  // on logout the device returns to its own local state; the playset
  // filter is logged-in-only UI, so it can't be left invisibly active
  const prevUserRef = React.useRef(null)
  React.useEffect(() => {
    if (prevUserRef.current && !user) {
      setInventory(startInventory())
      setOnlyIncompleteSets(false)
    }
    prevUserRef.current = user
  }, [user])

  // single persistence choke point. Logged out: localStorage, exactly
  // as before login existed. Logged in: cloud only — localStorage is
  // never written, so the pre-login local copy survives a decline of
  // the migration prompt.
  const persistUserRef = React.useRef(null)
  React.useEffect(() => {
    const prev = prevInventoryRef.current
    prevInventoryRef.current = inventory

    const isFullReplace = fullReplaceRef.current === inventory
    if (isFullReplace) {
      fullReplaceRef.current = null
    }

    // on the render where auth state flips, `inventory` still holds
    // the other mode's data (the cloud copy during logout, the device
    // copy during login) — persisting it would cross the streams, e.g.
    // writing the account's collection into localStorage over the
    // device copy. Skip; the reset/apply effects settle the state and
    // the next real change persists normally.
    if (persistUserRef.current !== user) {
      persistUserRef.current = user
      return
    }

    if (!user) {
      localInventory.store(inventory)
      return
    }
    // never queue before the first snapshot has been applied: a diff
    // against stale local state would push wrong absolute counts over
    // the cloud collection (the UI also gates editing on cloudLoaded)
    if (!cloudLoaded) {
      return
    }
    if (appliedFromCloudRef.current) {
      appliedFromCloudRef.current = false
      return
    }
    const sync = authCtx.getSync()
    if (!sync) {
      return
    }
    if (isFullReplace) {
      sync.replaceInventory(inventory).catch((err) => {
        console.error("cloud collection replace failed", err)
        window.alert(
          "Saving your collection to your account failed, please try uploading again.",
        )
      })
      return
    }
    if (!prev) {
      return
    }
    const changes = {}
    Object.keys(inventory).forEach((name) => {
      if (inventory[name] !== (prev[name] ?? 0)) {
        changes[name] = inventory[name]
      }
    })
    if (Object.keys(changes).length > 0) {
      sync.queueInventory(changes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory, user, cloudLoaded])

  /**
   * FILTER STATE
   */
  const [speed, setSpeed] = useState("any") // "any", fast, slow
  const [elements, setElements] = useState([])
  const [classes, setClasses] = useState([])
  const [types, setTypes] = useState([])
  const [subtypes, setSubtypes] = useState([])
  const [setNames, setSetNames] = useState([])
  const [keywords, setKeywords] = useState([])
  const [withLevelBonus, setWithLevelBonus] = useState(false)
  const [withClassBonus, setWithClassBonus] = useState(false)
  const [withElementBonus, setWithElementBonus] = useState(false)
  const [withFloatingMem, setWithFloatingMem] = useState(false)
  const [withAddPrep, setWithAddPrep] = useState(false)
  const [withUsePrep, setWithUsePrep] = useState(false)
  const [withSelfMill, setWithSelfMill] = useState(false)
  const [withOppMill, setWithOppMill] = useState(false)
  const [withGraveBanish, setWithGraveBanish] = useState(false)
  const [withMemoryBonus, setWithMemoryBonus] = useState(false)
  const [onlyChampElements, setOnlyChampElements] = useState(true)
  const [legality, setLegality] = useState("standard")
  const [metaTier, setMetaTier] = useState("all")
  const [onlyIncompleteSets, setOnlyIncompleteSets] = useState(false)
  const [rarities, setRarities] = useState([])
  const [onlyMultiClass, setOnlyMultiClass] = useState(false)

  const [sort, setSort] = useState("ELEMENT") // print, $cost, alpha, play cost, element
  const [sortDir, setSortDir] = useState("asc") // desc

  const [operator, setOperator] = useState(null) // =, <, >
  const [cost, setCost] = useState(0)

  const [powOp, setPowOp] = useState(null) // =, <, >
  const [power, setPower] = useState(0)

  const [lifeOp, setLifeOp] = useState(null) // =, <, >
  const [life, setLife] = useState(0)

  const [search, setSearch] = useState("")

  // open exclusions section:
  const [openAnti, setOpenAnti] = useState(false)
  // exclusion filters:
  const [anti_elements, setAnti_Elements] = useState([])
  const [anti_classes, setAnti_Classes] = useState([])
  const [anti_types, setAnti_Types] = useState([])
  const [anti_subtypes, setAnti_Subtypes] = useState([])
  const [anti_search, setAnti_Search] = useState("")
  const [anti_bonus, setAnti_Bonus] = useState(false)

  // defer the text searches so typing stays responsive: the inputs
  // update immediately while re-filtering runs at low priority.
  const deferredSearch = React.useDeferredValue(search)
  const deferredAntiSearch = React.useDeferredValue(anti_search)

  const focus = champCtx.focus

  const { champElements, championClasses, championClassesSet } = React.useMemo(
    () => {
      const elementsSet = new Set()
      const classesSet = new Set()
      ;(focus ?? []).forEach((c) => {
        elementsSet.add(c.element)
        if (ADVANCED_ELEMENTS.includes(c.element)) {
          elementsSet.add("EXALTED")
        }
        c.classes.forEach((cl) => {
          classesSet.add(cl)
        })
      })
      elementsSet.add("NORM")
      return {
        champElements: [...elementsSet],
        championClasses: [...classesSet],
        championClassesSet: classesSet,
      }
    },
    [focus],
  )

  const elementOpts = onlyChampElements ? champElements : ELEMENTS

  const cards = React.useMemo(() => {
    if (!focus) return []

    // copy so the sorts below never mutate the module-level array:
    let cards = [...allCards]

    if (ignoredSet && ignoredSet.size) {
      cards = cards.filter((c) => !ignoredSet.has(c.name))
    }

    if (legality === "standard") {
      cards = cards.filter((c) => c.computed.standard_legal)
    } else if (legality === "pantheon") {
      cards = cards.filter((c) => c.computed.pantheon_only)
    } else if (legality === "draft") {
      cards = cards.filter((c) => c.computed.draft_only)
    }

    if (onlyMultiClass) {
      cards = cards.filter((c) => c.classes.length > 1)
    }

    if (onlyIncompleteSets) {
      cards = cards.filter((c) => {
        const singleOnly = isSingleOnlyCard(c)
        const playSet = singleOnly ? 1 : 4
        return inventory[c.name] < playSet
      })
    }

    if (sort === "ELEMENT") {
      cards.sort((a, b) => {
        const nodes = getNodes(sortDir, a, b)
        const diff =
          fallbackInfinity(ELEMENT_ORDER[nodes.first.element]) -
          fallbackInfinity(ELEMENT_ORDER[nodes.second.element])
        if (diff === 0) {
          return (
            fallbackInfinity(
              nodes.first.costMemory ?? nodes.first.costReserve,
            ) -
            fallbackInfinity(
              nodes.second.costMemory ?? nodes.second.costReserve,
            )
          )
        }
        return diff
      })
    }
    if (sort === "PRINT RUN") {
      cards.sort((a, b) => {
        const nodes = getNodes(sortDir, a, b)
        const diff =
          fallbackInfinity(nodes.first.computed.totalPrints) -
          fallbackInfinity(nodes.second.computed.totalPrints)
        if (diff === 0) {
          const firstRegalia = a.types.includes("REGALIA") ? 1 : 0
          const secondRegalia = b.types.includes("REGALIA") ? 1 : 0
          // then alphabetically
          const regDiff = secondRegalia - firstRegalia
          if (regDiff === 0) {
            return nodes.first.name
              .toLowerCase()
              .localeCompare(nodes.second.name.toLowerCase())
          }
        }
        return diff
      })
    }
    if (sort === "TCGPLAYER COST") {
      cards.sort((a, b) => {
        const nodes = getNodes(sortDir, a, b)
        return (
          fallbackInfinity(nodes.first.computed.tcgLowestPrice) -
          fallbackInfinity(nodes.second.computed.tcgLowestPrice)
        )
      })
    }
    if (sort === "WIN RATE") {
      cards.sort((a, b) => {
        const nodes = getNodes(sortDir, a, b)
        // cards with no 60-day play count as 0% win rate:
        return (
          (nodes.first.computed.meta?.wr60 ?? 0) -
          (nodes.second.computed.meta?.wr60 ?? 0)
        )
      })
    }
    if (sort === "ALPHABETICALLY") {
      cards.sort((a, b) => {
        const nodes = getNodes(sortDir, a, b)
        return nodes.first.name
          .toLowerCase()
          .localeCompare(nodes.second.name.toLowerCase())
      })
    }
    if (sort === "PLAY COST") {
      cards.sort((a, b) => {
        const nodes = getNodes(sortDir, a, b)
        const firstCost = nodes.first.costMemory ?? nodes.first.costReserve
        const secondCost = nodes.second.costMemory ?? nodes.second.costReserve
        const diff = firstCost - secondCost
        // always place regalias first
        if (diff === 0) {
          const firstRegalia = a.types.includes("REGALIA") ? 1 : 0
          const secondRegalia = b.types.includes("REGALIA") ? 1 : 0
          // then alphabetically
          const regDiff = secondRegalia - firstRegalia
          if (regDiff === 0) {
            return nodes.first.name
              .toLowerCase()
              .localeCompare(nodes.second.name.toLowerCase())
          }
        }
        return diff
      })
    }
    if (sort === "INVENTORY") {
      cards.sort((a, b) => {
        const nodes = getNodes(sortDir, a, b)
        const firstInv = inventory[nodes.first.name] ?? 0
        const secondInv = inventory[nodes.second.name] ?? 0
        return firstInv - secondInv
      })
    }

    // forced filter: lineup elements
    cards = cards.filter((c) => {
      return elementOpts.includes(c.element)
    })

    if (powOp !== null) {
      cards = cards.filter((c) => {
        if (typeof c.power === "number") {
          if (powOp === "=") return c.power === power
          if (powOp === ">") return c.power > power
          if (powOp === "<") return c.power < power
        }
        return false
      })
    }

    if (lifeOp !== null) {
      cards = cards.filter((c) => {
        if (typeof c.life === "number") {
          if (lifeOp === "=") return c.life === life
          if (lifeOp === ">") return c.life > life
          if (lifeOp === "<") return c.life < life
        }
        return false
      })
    }

    if (operator !== null) {
      cards = cards.filter((c) => {
        const cardCost =
          [c.costMemory, c.costReserve].find((n) => Number.isInteger(n)) ??
          null
        if (typeof cardCost === "number") {
          if (operator === "=") return cardCost === cost
          if (operator === ">") return cardCost > cost
          if (operator === "<") return cardCost < cost
        }
        return false
      })
    }

    if (speed !== "any") {
      cards = cards.filter((c) => c.speed === speed)
    }

    if (metaTier !== "all") {
      const tier = META_TIERS.find((t) => t.value === metaTier)
      // no-op rather than an empty grid when the tier is unknown or
      // the data shipped without any 60-day win rates:
      const filterable =
        tier?.inTier && !(tier.needsCutoffs && META_TIER_CUTOFFS.top5 == null)
      if (filterable) {
        // cards with no 60-day play count as 0% win rate:
        cards = cards.filter((c) => tier.inTier(c.computed.meta?.wr60 ?? 0))
      }
    }

    if (rarities.length) {
      cards = cards.filter((c) => {
        return rarities.some((r) => c.computed.rarities.includes(r))
      })
    }

    if (keywords.length) {
      cards = cards.filter((c) => {
        return keywords.every((k) => c.computed.keywords[k])
      })
    }

    if (elements.length) {
      cards = cards.filter((c) => {
        // no longer supports OR operation in order for
        // searches that include EXALTED to work intuitively:
        if (!c.elements.length) return false
        return elements.every((pick) => c.elements.includes(pick))
      })
    }

    if (classes.length) {
      cards = cards.filter((c) => {
        return classes.every((cs) => c.classes.includes(cs))
        // return c.classes.some((cardClass) => classes.includes(cardClass))
      })
    }

    if (types.length) {
      cards = cards.filter((c) => {
        return types.every((t) => c.types.includes(t))
        // return c.types.some((type) => types.includes(type))
      })
    }

    if (subtypes.length) {
      cards = cards.filter((c) => {
        return subtypes.every((st) => c.subtypes.includes(st))
        // return c.subtypes.some((subtype) => subtypes.includes(subtype))
      })
    }

    if (setNames.length) {
      cards = cards.filter((c) => {
        return c.computed.setNames.some((setName) => setNames.includes(setName))
      })
    }

    if (withMemoryBonus) {
      cards = cards.filter((c) => {
        return c.computed.memoryBonus
      })
    }

    if (withLevelBonus) {
      cards = cards.filter((c) => {
        return c.computed.levelBonus
      })
    }

    // forced to filter based on champion classes
    if (withClassBonus) {
      cards = cards.filter((c) => {
        return (
          c.computed.classBonus &&
          championClasses.some((cc) => {
            return c.classes.includes(cc)
          })
        )
      })
    }

    if (withElementBonus) {
      cards = cards.filter((c) => {
        return (
          c.computed.elementBonus &&
          // forced to filter based on champion element
          (onlyChampElements ? champElements.includes(c.element) : true)
        )
      })
    }

    if (withSelfMill) {
      cards = cards.filter((c) => {
        const millData = MILLCARDS[c.name]
        return !!millData && !!millData.self
      })
    }

    if (withOppMill) {
      cards = cards.filter((c) => {
        const millData = MILLCARDS[c.name]
        return !!millData && !!millData.opp
      })
    }

    if (withGraveBanish) {
      cards = cards.filter((c) => {
        return !!GRAVEBANISH[c.name]
      })
    }

    if (withAddPrep) {
      cards = cards.filter((c) => {
        const prepData = PREPCOUNT[c.name]
        return !!prepData && !!prepData.adds
      })
    }

    if (withUsePrep) {
      cards = cards.filter((c) => {
        const prepData = PREPCOUNT[c.name]
        return !!prepData && !!prepData.uses
      })
    }

    if (withFloatingMem) {
      cards = cards.filter((c) => {
        // deprecated pattern,
        // must now use manual file:
        // c.computed.floating

        const fmData = FLOATING[c.name]
        if (!fmData) return false
        if (fmData.classBonus) {
          return c.classes.some((cClass) => championClassesSet.has(cClass))
        }
        if (fmData.pure) {
          return true
        }
        // champion-specific floating memory (e.g. vanitas, merlin, ciel):
        // any remaining key is the champion's base name. Match it against
        // the lineup's champion names (the part before the comma) to avoid
        // substring false positives (e.g. "rai" inside "Lorraine").
        return Object.keys(fmData).some((keyName) => {
          if (keyName === "classBonus" || keyName === "pure") return false
          const key = keyName.toLowerCase()
          return focus.some((fc) => {
            const base = fc.name.split(",")[0].trim().toLowerCase()
            return base === key
          })
        })
      })
    }

    if (deferredSearch) {
      const text = deferredSearch.toLowerCase()
      cards = cards.filter((c) => {
        return Boolean(
          c.computed.name.includes(text) ||
          c.computed.rawEffect?.replace(/\(.*?\)/g, "").includes(text) ||
          c.computed.backName?.includes(text) ||
          c.computed.backEffect?.replace(/\(.*?\)/g, "").includes(text),
        )
      })
    }

    // exclusion filters:
    if (anti_elements.length) {
      cards = cards.filter((c) => {
        return !anti_elements.includes(c.element)
      })
    }
    if (anti_types.length) {
      cards = cards.filter((c) => {
        return anti_types.every((t) => !c.types.includes(t))
      })
    }
    if (anti_subtypes.length) {
      cards = cards.filter((c) => {
        return anti_subtypes.every((t) => !c.subtypes.includes(t))
      })
    }
    if (anti_classes.length) {
      cards = cards.filter((c) => {
        return anti_classes.every((cs) => !c.classes.includes(cs))
      })
    }
    if (deferredAntiSearch) {
      const text = deferredAntiSearch.toLowerCase()
      cards = cards.filter((c) => {
        return !Boolean(
          c.computed.backName?.includes(text) ||
          c.computed.rawEffect?.replace(/\(.*?\)/g, "").includes(text) ||
          c.computed.name.includes(text) ||
          c.computed.backEffect?.replace(/\(.*?\)/g, "").includes(text),
        )
      })
    }
    if (anti_bonus) {
      cards = cards.filter((c) => {
        return !c.computed.classBonus
      })
    }

    return cards
  }, [
    focus,
    ignoredSet,
    legality,
    onlyMultiClass,
    onlyIncompleteSets,
    inventory,
    sort,
    sortDir,
    elementOpts,
    powOp,
    power,
    lifeOp,
    life,
    operator,
    cost,
    speed,
    metaTier,
    rarities,
    keywords,
    elements,
    classes,
    types,
    subtypes,
    setNames,
    withMemoryBonus,
    withLevelBonus,
    withClassBonus,
    championClasses,
    withElementBonus,
    onlyChampElements,
    champElements,
    withSelfMill,
    withOppMill,
    withGraveBanish,
    withAddPrep,
    withUsePrep,
    withFloatingMem,
    championClassesSet,
    deferredSearch,
    anti_elements,
    anti_types,
    anti_subtypes,
    anti_classes,
    deferredAntiSearch,
    anti_bonus,
  ])

  function handleClearExclusions() {
    setOpenAnti(false)
    setAnti_Elements([])
    setAnti_Classes([])
    setAnti_Types([])
    setAnti_Subtypes([])
    setAnti_Search("")
    setAnti_Bonus(false)
  }

  function handleClear() {
    setOnlyChampElements(true)
    setLegality("standard")
    setSearch("")
    setSpeed("any")
    setMetaTier("all")
    setElements([])
    setClasses([])
    setTypes([])
    setSubtypes([])
    setSetNames([])
    setKeywords([])
    setRarities([])

    setOperator(null)
    setCost(0)
    setPowOp(null)
    setPower(0)
    setLifeOp(null)
    setLife(0)

    setSort("ELEMENT")
    setSortDir("asc")

    setWithClassBonus(false)
    setWithFloatingMem(false)
    setWithElementBonus(false)
    setWithLevelBonus(false)
    setWithMemoryBonus(false)
    setWithAddPrep(false)
    setWithUsePrep(false)
    setWithSelfMill(false)
    setWithOppMill(false)
    setWithGraveBanish(false)

    setOnlyIncompleteSets(false)
    setOnlyMultiClass(false)

    handleClearExclusions()
  }

  if (!champCtx.focus) {
    return (
      <CardsContext.Provider value={{ handleClear }}>
        {children}
      </CardsContext.Provider>
    )
  }

  // cleanup elements selection in case user added elements
  // that aren't part of selected champ lineage:
  if (onlyChampElements) {
    if (!elements.every((e) => champElements.includes(e))) {
      setElements((list) =>
        list.filter((el) => {
          return champElements.includes(el)
        }),
      )
    }
  }

  const classOpts = CLASSES
  const typeOpts = TYPES
  const setNameOpts = SET_NAMES

  // we used to need to filter
  // here, but not anymore:
  const subtypeOpts = SUBTYPES

  return (
    <CardsContext.Provider
      value={{
        search,
        setSearch,

        speed,
        setSpeed,

        elements,
        setElements,
        elementOpts,

        classes,
        setClasses,
        classOpts,

        withClassBonus,
        setWithClassBonus,
        withElementBonus,
        setWithElementBonus,
        withFloatingMem,
        setWithFloatingMem,
        withLevelBonus,
        setWithLevelBonus,
        withMemoryBonus,
        setWithMemoryBonus,

        withSelfMill,
        setWithSelfMill,
        withOppMill,
        setWithOppMill,

        withGraveBanish,
        setWithGraveBanish,

        withAddPrep,
        setWithAddPrep,
        withUsePrep,
        setWithUsePrep,

        cards,

        onlyChampElements,
        setOnlyChampElements,

        legality,
        setLegality,

        metaTier,
        setMetaTier,

        subtypes,
        setSubtypes,
        subtypeOpts,

        types,
        setTypes,
        typeOpts,

        setNames,
        setSetNames,
        setNameOpts,

        keywords,
        setKeywords,
        keywordOpts: KEYWORDS,

        cost,
        setCost,
        operator,
        setOperator,

        powOp,
        setPowOp,
        power,
        setPower,

        lifeOp,
        setLifeOp,
        life,
        setLife,

        sort,
        setSort,
        sortOpts,
        sortDir,
        setSortDir,
        sortDirOpts,

        handleClear,

        inventory,
        setInventory: setInventoryNormalized,
        replaceInventory,
        initInventory,
        inventoryEnabled,
        setInventoryEnabled,
        estInventoryValue,
        estMissingPlaysetValue,
        fullCollectionValue,
        allCardsMap,

        onlyIncompleteSets,
        setOnlyIncompleteSets,

        rarities,
        setRarities,
        rarityOpts,

        onlyMultiClass,
        setOnlyMultiClass,

        // exclusions:
        handleClearExclusions,
        openAnti,
        setOpenAnti,
        anti_elements,
        setAnti_Elements,
        anti_classes,
        setAnti_Classes,
        anti_types,
        setAnti_Types,
        anti_subtypes,
        setAnti_Subtypes,
        anti_search,
        setAnti_Search,
        anti_bonus,
        setAnti_Bonus,
      }}
    >
      {children}
    </CardsContext.Provider>
  )
}

export const useCardsContext = () => {
  const value = useContext(CardsContext)
  if (!value) {
    throw new Error("Called useCardsContext() outside of provider")
  }
  return value
}
